import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { translateRoute, getRoute } from '@/lib/i18nRoutes';
import { translations, Translations, Language } from '@/i18n/translations';

// Lazy import to avoid circular dependency
const getSupabase = () => import('@/integrations/supabase/client').then(m => m.supabase);

// Re-export for consumers
export type { Language, Translations };

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  switchLanguage: (lang: Language, navigate: (path: string) => void, currentPath: string) => void;
  getLocalizedRoute: (key: string) => string;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'dqs-academy-language';

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'en' || stored === 'pt-BR' || stored === 'es') {
        return stored;
      }
    }
    return 'pt-BR';
  });

  const persistToSupabase = useCallback(async (lang: Language) => {
    try {
      const sb = await getSupabase();
      const { data: { session } } = await sb.auth.getSession();
      if (session?.user) {
        await sb.from('users').update({ language: lang }).eq('id', session.user.id);
      }
    } catch { /* ignore */ }
  }, []);

  const setLanguage = async (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
    persistToSupabase(lang);
  };

  const switchLanguage = useCallback(async (lang: Language, navigate: (path: string) => void, currentPath: string) => {
    const oldLang = language;
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);

    persistToSupabase(lang);

    // Translate the current route to the new language
    const newPath = translateRoute(currentPath, oldLang, lang);
    if (newPath !== currentPath) {
      navigate(newPath);
    }
  }, [language, persistToSupabase]);

  const getLocalizedRoute = useCallback((key: string): string => {
    return getRoute(key, language);
  }, [language]);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'pt-BR' || stored === 'es') {
      setLanguageState(stored);
    }
  }, []);

  // Aplica o idioma salvo no PERFIL do usuário ao entrar.
  //
  // A coluna users.language já era GRAVADA (persistToSupabase), mas nunca lida
  // de volta — então quem tinha o perfil em inglês continuava caindo na
  // plataforma em português. Ruim principalmente pra quem acessa de fora do
  // Brasil, que não tem por que adivinhar o seletor de idioma no cabeçalho.
  //
  // Só vale quando o usuário AINDA NÃO escolheu idioma neste navegador: escolha
  // explícita (localStorage) sempre ganha do perfil. E não gravamos no
  // localStorage aqui — o perfil segue mandando até ele escolher na mão.
  useEffect(() => {
    let cancelado = false;

    const aplicarDoPerfil = async () => {
      if (localStorage.getItem(STORAGE_KEY)) return;
      try {
        const sb = await getSupabase();
        const { data: { session } } = await sb.auth.getSession();
        if (!session?.user) return;
        const { data } = await sb
          .from('users')
          .select('language')
          .eq('id', session.user.id)
          .maybeSingle();
        const lang = data?.language;
        if (!cancelado && (lang === 'en' || lang === 'es' || lang === 'pt-BR')) {
          setLanguageState(lang);
        }
      } catch { /* idioma é preferência, nunca deve quebrar a tela */ }
    };

    aplicarDoPerfil();

    // O login costuma acontecer DEPOIS da montagem (o usuário chega deslogado),
    // então reagir ao SIGNED_IN é o que faz isso valer no primeiro acesso.
    let unsub: (() => void) | undefined;
    getSupabase().then((sb) => {
      const { data } = sb.auth.onAuthStateChange((event) => {
        if (event === 'SIGNED_IN') aplicarDoPerfil();
      });
      unsub = () => data.subscription.unsubscribe();
    }).catch(() => { /* ignore */ });

    return () => { cancelado = true; unsub?.(); };
  }, []);

  const value = {
    language,
    setLanguage,
    switchLanguage,
    getLocalizedRoute,
    t: translations[language],
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
